import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TIMETRACK_EVENT, useTimeTrackAccess, useTimeTrackTargets } from "@/hooks/use-timetrack";
import type { TimeTrackOpenIntent } from "@/hooks/use-timetrack";
import { PersonalTimer } from "./personal-timer";
import { TimeEntryEditor } from "./time-entry-editor";
import { TimeTrackView } from "./time-track-view";
import "./timetrack.css";

function ManualEntryHost({ intent, onClose }: { intent: TimeTrackOpenIntent; onClose: () => void }) {
  const resources = useTimeTrackTargets();
  if (resources.data) return <TimeEntryEditor intent={{ mode: "manual", initial: intent, workDate: intent.workDate }} resources={resources.data} onClose={onClose} />;
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent className="tt tt-dialog tt-editor"><DialogTitle>Add missing time</DialogTitle>
      <DialogDescription className="tt-subtle">Loading your authorized projects and assigned artists.</DialogDescription>
      {resources.isError ? <div role="alert" className="tt-error">{resources.error.message}<button className="tt-button" onClick={() => resources.refetch()}>Retry</button></div>
        : <div className="tt-skeleton" role="status" aria-label="Loading work items" />}
    </DialogContent>
  </Dialog>;
}

function TimeTrackAuthenticatedHost() {
  const [intent, setIntent] = useState<(TimeTrackOpenIntent & { key: number }) | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TimeTrackOpenIntent>).detail || {};
      setIntent({ ...detail, targetId: detail.targetId == null ? undefined : String(detail.targetId), key: Date.now() });
    };
    window.addEventListener(TIMETRACK_EVENT, handler);
    return () => window.removeEventListener(TIMETRACK_EVENT, handler);
  }, []);
  return <>
    <PersonalTimer />
    {intent?.view === "manual" ? <ManualEntryHost key={intent.key} intent={intent} onClose={() => setIntent(null)} />
      : intent && <Dialog open onOpenChange={open => { if (!open) setIntent(null); }}>
        <DialogContent className="tt tt-dialog" aria-describedby="timetrack-ledger-description">
          <DialogTitle className="sr-only">TimeTrack — My Time and project time</DialogTitle>
          <DialogDescription id="timetrack-ledger-description" className="sr-only">Review recorded work, manage your sessions, and correct missing time.</DialogDescription>
          <TimeTrackView key={intent.key} initial={intent} />
        </DialogContent>
      </Dialog>}
  </>;
}

export function TimeTrackHost() {
  const { enabled, user } = useTimeTrackAccess();
  return enabled ? <TimeTrackAuthenticatedHost key={user!.id} /> : null;
}
